
import React, { useContext, useState, useEffect } from "react";
import { DollarSign, AlertCircle, Clock } from "lucide-react";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import AlertModal from "../../components/AlertModal";
import LoadErrorState from "../../components/LoadErrorState";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

import translations from "./Constant/AdvancementManagement.translations";
import getStyles from "./Style/AdvancementManagement.styles";
import useAdvancementManagement from "./hooks/useAdvancementManagement";

import StatsGrid from "./components/StatsGrid";
import RequestCard from "./components/RequestCard";
import ProcessModal from "./components/ProcessModal";
import DetailsModal from "./components/DetailsModal";

import {
  CheckCircle,
  XCircle,
  DollarSign as DollarIcon,
} from "lucide-react";

const getStatusIcon = (status) => {
  switch (status) {
    case "approved":   return <CheckCircle size={18} color="#10b981" />;
    case "rejected":   return <XCircle size={18} color="#ef4444" />;
    case "disbursed":  return <DollarIcon size={18} color="#2e7cba" />;
    case "completed":  return <CheckCircle size={18} color="#8b5cf6" />;
    case "pending":    return <Clock size={18} color="#f59e0b" />;
    default:           return <AlertCircle size={18} />;
  }
};

const AdvancementManagementPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  const isRTL = language === "ar";
  const t = translations[language] || translations.en;
  const styles = getStyles(theme, isRTL);

  // ── AlertModal state ───────────────────────────────────────────────────────
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
    onConfirm: null,
  });

  const showAlert = (title, message, variant = "info", onConfirm = null, showCancel = false) => {
    setAlertModal({ open: true, title, message, variant, showCancel, onConfirm });
  };

  const closeAlert = () => setAlertModal((prev) => ({ ...prev, open: false }));

  // ── Hook ───────────────────────────────────────────────────────────────────
  const {
    requests,
    loading,
    error,
    stats,
    activeTab,
    setActiveTab,
    selectedRequest,
    setSelectedRequest,
    showProcessModal,
    setShowProcessModal,
    showDetailsModal,
    setShowDetailsModal,
    repaymentHistory,
    formData,
    setFormData,
    isProcessing,
    getEmployeeName,
    getStatusText,
    getUrgencyText,
    formatCurrency,
    fetchRequests,
    fetchRequestById,
    fetchRepaymentHistory,
    handleApprove,
    handleReject,
    handleDisburse,
    resetForm,
  } = useAdvancementManagement(language, showAlert, t);

  // ── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!loading) setPageReady(true);
  }, [loading]);

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabs = [
    { id: "all",       label: t.all,       count: requests.length },
    { id: "pending",   label: t.pending,   count: stats.totalPending },
    { id: "approved",  label: t.approved,  count: stats.totalApproved },
    { id: "disbursed", label: t.disbursed, count: stats.totalDisbursed },
    { id: "completed", label: t.completed, count: stats.totalCompleted },
    { id: "rejected",  label: t.rejected,  count: stats.totalRejected },
  ];

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && loading) {
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل طلبات السلف..." : "Loading advancement requests..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Stats skeleton — statsGrid carries a large negative marginTop meant to
            tuck it under the real page header; neutralize it here so it doesn't
            slide up over the LoadingPill above it. */}
        <div style={{ ...styles.statsGrid, marginTop: 0 }}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={styles.statCard} />
          ))}
        </div>

        {/* Main card skeleton */}
        <div className="nx-section-card" style={styles.card}>
          <SkeletonBox width="220px" height="28px" style={{ marginBottom: "24px" }} />

          {/* Tabs skeleton */}
          <div className="nx-tabs-scroll" style={styles.tabContainer}>
            {tabs.map((tab) => (
              <SkeletonBox key={tab.id} width="110px" height="36px" style={{ borderRadius: "8px", marginBottom: "8px" }} />
            ))}
          </div>

          {/* Request cards skeleton */}
          {[1, 2, 3].map((i) => (
            <div key={i} style={styles.requestCard}>
              <SkeletonBox width="200px" height="22px" style={{ marginBottom: "14px" }} />
              <SkeletonBox width="100%" height="16px" style={{ marginBottom: "10px" }} />
              <SkeletonBox width="80%" height="16px" style={{ marginBottom: "10px" }} />
              <SkeletonBox width="60%" height="16px" style={{ marginBottom: "16px" }} />
              <div style={{ display: "flex", gap: "8px" }}>
                <SkeletonBox width="110px" height="36px" style={{ borderRadius: "10px" }} />
                <SkeletonBox width="130px" height="36px" style={{ borderRadius: "10px" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Stats */}
      <StatsGrid t={t} styles={styles} stats={stats} language={language} />

      {/* Main card */}
      <div className="nx-section-card" style={styles.card}>
        <h2 className="nx-page-title" style={styles.title}>
          <DollarSign size={24} />
          {t.title}
        </h2>

        {/* Tabs */}
        <div className="nx-tabs-scroll" style={styles.tabContainer}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={styles.tab(activeTab === tab.id)}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label} {tab.count > 0 && `(${tab.count})`}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div style={styles.loadingState}>
            <Clock size={64} style={{ marginBottom: "20px", opacity: 0.3 }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
              {t.loading}
            </h3>
            <p>{t.pleaseWait}</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <LoadErrorState variant="inline" error={error} onRetry={() => fetchRequests(activeTab)} />
        )}

        {/* Empty */}
        {!loading && !error && requests.length === 0 && (
          <div style={styles.emptyState}>
            <DollarSign size={64} style={{ marginBottom: "20px", opacity: 0.3 }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px",
              color: theme === "light" ? "#374151" : "rgba(255,255,255,0.8)" }}>
              {t.noRequests}
            </h3>
            <p>{t.noRequestsFound}</p>
          </div>
        )}

        {/* Request cards */}
        {!loading && !error && requests.map((request) => (
          <RequestCard
            key={request.id}
            request={request}
            t={t}
            styles={styles}
            language={language}
            getEmployeeName={getEmployeeName}
            getStatusText={getStatusText}
            getUrgencyText={getUrgencyText}
            getStatusIcon={getStatusIcon}
            formatCurrency={formatCurrency}
            onViewDetails={(req) => {
              setSelectedRequest(req);
              fetchRepaymentHistory(req.id);
              setShowDetailsModal(true);
            }}
            onProcess={async (req) => {
              const freshRequest = await fetchRequestById(req.id);
              const nextRequest = freshRequest || req;
              setSelectedRequest(nextRequest);
              setFormData((prev) => ({ ...prev, status: "" }));
              setShowProcessModal(true);
            }}
            onDisburse={async (req) => {
              const freshRequest = await fetchRequestById(req.id);
              const nextRequest = freshRequest || req;
              setSelectedRequest(nextRequest);
              setFormData((prev) => ({ ...prev, status: "disburse" }));
              setShowProcessModal(true);
            }}
          />
        ))}
      </div>

      {/* Process Modal */}
      {showProcessModal && selectedRequest && (
        <ProcessModal
          t={t}
          styles={styles}
          isRTL={isRTL}
          selectedRequest={selectedRequest}
          formData={formData}
          setFormData={setFormData}
          getEmployeeName={getEmployeeName}
          formatCurrency={formatCurrency}
          onApprove={handleApprove}
          onReject={handleReject}
          onDisburse={handleDisburse}
          onClose={() => { setShowProcessModal(false); resetForm(); }}
          isProcessing={isProcessing}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <DetailsModal
          t={t}
          styles={styles}
          theme={theme}
          isRTL={isRTL}
          language={language}
          selectedRequest={selectedRequest}
          repaymentHistory={repaymentHistory}
          getEmployeeName={getEmployeeName}
          getStatusText={getStatusText}
          getStatusIcon={getStatusIcon}
          getUrgencyText={getUrgencyText}
          formatCurrency={formatCurrency}
          onClose={() => setShowDetailsModal(false)}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={() => {
          if (alertModal.onConfirm) alertModal.onConfirm();
          closeAlert();
        }}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default AdvancementManagementPage;